import queryString from "query-string";

/** sessionStorage keys for donation landing URL attribution */
export const LANDING_URL_SESSION_KEYS = {
  original: "landing_url_original",
  preAuth0: "landing_url_pre_auth0",
  postAuth0: "landing_url_post_auth0",
  /** @deprecated use LANDING_URL_SESSION_KEYS.original */
  legacy: "landing_url",
} as const;

/** Query params preserved in landing URL snapshots (matches App.Router). */
export const LANDING_URL_QUERY_KEYS = [
  "utm_campaign",
  "utm_medium",
  "utm_source",
  "utm_content",
  "utm_term",
  "term",
  "amount",
  "cid",
  "sponsorship",
  "layout",
] as const;

const LOG_PREFIX = "[landing-url]";

const shouldLogToConsole = () =>
  import.meta.env.DEV || sessionStorage.getItem("landing_url_debug") === "1";

export const logLandingUrls = (
  event: string,
  data: Record<string, unknown> = {}
) => {
  if (shouldLogToConsole()) {
    console.info(LOG_PREFIX, event, data);
  }
};

const normalizeParams = (params: Record<string, unknown> = {}) =>
  Object.keys(params).reduce(
    (acc, key) => ({
      ...acc,
      [key.toLowerCase()]: params[key],
    }),
    {} as Record<string, unknown>
  );

/** UTM-whitelisted donate URL — no auth codes, email, or other PII query params. */
export const buildWhitelistedLandingUrl = (
  params: Record<string, unknown> = {},
  pathname = window.location.pathname
) => {
  const normalized = normalizeParams(params);
  const picked = LANDING_URL_QUERY_KEYS.reduce((acc, key) => {
    const val = normalized[key];
    if (val != null && val !== "") {
      acc[key] = val;
    }
    return acc;
  }, {} as Record<string, unknown>);
  const query = queryString.stringify(picked);
  return `${window.location.protocol}//${window.location.hostname}${pathname}${
    query ? `?${query}` : ""
  }`;
};

export const buildWhitelistedLandingUrlFromLocation = (
  pathname = window.location.pathname
) => {
  const parsed = queryString.parse(window.location.search);
  return buildWhitelistedLandingUrl(parsed, pathname);
};

const setOnce = (key: string, url: string, event: string) => {
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, url);
    logLandingUrls(event, { url });
  }
};

/** First page load in this tab/session. */
export const captureOriginalLandingUrl = () => {
  const url = buildWhitelistedLandingUrlFromLocation();
  setOnce(LANDING_URL_SESSION_KEYS.original, url, "original_captured");
  setOnce(LANDING_URL_SESSION_KEYS.legacy, url, "legacy_captured");
};

/**
 * Post-Auth0 landing snapshot from restored campaign params (not raw callback URL).
 * Pass merged `initialUrlParams` so code/state are excluded.
 */
export const capturePostAuth0LandingUrl = (
  initialUrlParams: Record<string, unknown> = {}
) => {
  const url = buildWhitelistedLandingUrl(
    initialUrlParams,
    window.location.pathname
  );
  setOnce(LANDING_URL_SESSION_KEYS.postAuth0, url, "post_auth0_captured");
};

/** URL immediately before redirecting to Auth0 — updated on each login attempt. */
export const capturePreAuth0LandingUrl = () => {
  const url = buildWhitelistedLandingUrlFromLocation();
  sessionStorage.setItem(LANDING_URL_SESSION_KEYS.preAuth0, url);
  logLandingUrls("pre_auth0_captured", { url });
};

export const prepareAuth0Redirect = (
  initialUrlParams: Record<string, unknown> = {}
) => {
  capturePreAuth0LandingUrl();
  const normalized = normalizeParams(initialUrlParams);
  const { code, state, ...rest } = normalized;
  const redirectQuery = queryString.stringify(rest);
  if (redirectQuery) {
    sessionStorage.setItem("redirectQuery", "?" + redirectQuery);
  }
};

export const getLandingUrlPayload = () => {
  const original =
    sessionStorage.getItem(LANDING_URL_SESSION_KEYS.original) ||
    sessionStorage.getItem(LANDING_URL_SESSION_KEYS.legacy);
  return {
    landingUrl: original,
    preAuth0LandingUrl: sessionStorage.getItem(
      LANDING_URL_SESSION_KEYS.preAuth0
    ),
    postAuth0LandingUrl: sessionStorage.getItem(
      LANDING_URL_SESSION_KEYS.postAuth0
    ),
  };
};

export const logLandingUrlsForDonation = () => {
  logLandingUrls("donation_submit", getLandingUrlPayload());
};
